import { useTheme } from "expo-router/react-navigation";
import { Check, Copy } from "lucide-react-native";
import { ComponentRef, ReactNode, useRef, useState } from "react";
import { View } from "react-native";

import {
  copyChartToClipboard,
  copyElementAsImage,
} from "@/components/questions/results/copyChartToClipboard";
import { Box, Pressable } from "@/components/ui";

/* ── Types ─────────────────────────────────────────────── */

interface UseCopyableChartOptions {
  /** Set for non-SVG visualizations (e.g. the HTML `Heatmap`). These can't
      be serialized as SVG, so they're rasterized via html2canvas instead. */
  raster?: boolean;
}

interface UseCopyableChart {
  chartRef: React.RefObject<ComponentRef<typeof View> | null>;
  copyButton: ReactNode;
}

/* ── Hook ───────────────────────────────────────────────── */

/**
 * Shared hook used by section-level report cards to copy their chart
 * to the clipboard. Returns:
 *  - `chartRef` — attach to the `View` wrapping the chart (so only the
 *    chart itself, not the card title/subtitle, is captured).
 *  - `copyButton` — a pre-positioned ReactNode that fades in on hover.
 *    Drop it as a direct child of the surrounding Card; the Card must
 *    carry the `group` Tailwind class for the hover transition.
 *
 * Pass `{ raster: true }` for heatmaps and other non-SVG charts.
 */
export function useCopyableChart(
  options?: UseCopyableChartOptions
): UseCopyableChart {
  const raster = options?.raster ?? false;
  const chartRef = useRef<ComponentRef<typeof View>>(null);
  const [copied, setCopied] = useState(false);
  const theme = useTheme();

  const copyVisual = async () => {
    const element = chartRef.current as unknown as HTMLElement;
    if (!element) return;
    const flash = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    if (raster) {
      await copyElementAsImage(element, flash);
    } else {
      await copyChartToClipboard(element, flash);
    }
  };

  const copyButton = (
    <Box className="absolute z-10 opacity-0 group-hover:opacity-100 transition-opacity top-4 right-4">
      <Pressable onPress={copyVisual}>
        <Box className="p-2 rounded-lg bg-background-100 dark:bg-background-800 hover:bg-background-200 dark:hover:bg-background-700 transition-colors">
          {copied ? (
            <Check color="rgb(var(--color-success-500))" size={16} />
          ) : (
            <Copy color={theme.colors.text} size={16} />
          )}
        </Box>
      </Pressable>
    </Box>
  );

  return { chartRef, copyButton };
}
